/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { LEASE_STATE_STYLES, type LeaseState } from './lease-states';

/**
 * The five board columns, and the lease states each one covers.
 *
 * Five rather than seven: `active` and `extended` are the same thing to a
 * broker scanning the board — a lease that is running — and the backend
 * already treats `extended` as running. `terminated` and `closed` pair for
 * the mirror reason: both are over, and the difference between "rescinded"
 * and "wound up" is a detail of the record, not a different pile of work.
 * The badge on each card still names the exact state, so the grouping
 * costs nothing a reader needs.
 *
 * `cancelled` stays alone. A cancelled lease is one that never ran at all,
 * which is not the same outcome as one that ran and ended, and folding the
 * two would tell a broker their contract closed when it was called off.
 */
export const LEASE_BOARD_COLUMNS = [
  { id: 'draft', states: ['draft'] },
  { id: 'running', states: ['active', 'extended'] },
  { id: 'suspended', states: ['suspended'] },
  { id: 'ended', states: ['terminated', 'closed'] },
  { id: 'cancelled', states: ['cancelled'] },
] as const satisfies ReadonlyArray<{ id: string; states: readonly LeaseState[] }>;

export type LeaseBoardColumnId = (typeof LEASE_BOARD_COLUMNS)[number]['id'];

/**
 * Accent per column, borrowed from the badge palette of the column's
 * leading state so a card's badge and the column above it never disagree
 * about what colour that part of the lifecycle is.
 */
export const LEASE_BOARD_COLUMN_STYLES: Record<
  LeaseBoardColumnId, { bg: string; color: string }
> = {
  draft: LEASE_STATE_STYLES.draft,
  running: LEASE_STATE_STYLES.active,
  suspended: LEASE_STATE_STYLES.suspended,
  ended: LEASE_STATE_STYLES.closed,
  cancelled: LEASE_STATE_STYLES.cancelled,
};

/**
 * Which column a state belongs to, or null when it is off the board.
 *
 * @param state - The lease's current state.
 * @returns The column id, or null for a state no column covers.
 */
export function columnForLeaseState(state: string): LeaseBoardColumnId | null {
  const found = LEASE_BOARD_COLUMNS.find(
    (column) => (column.states as readonly string[]).includes(state),
  );
  return found ? found.id : null;
}

/**
 * Group leases into the board's columns, preserving the incoming order.
 *
 * A lease whose state no column covers — a value left by an older
 * migration, say — is dropped from the board rather than piled into a
 * catch-all column: the table view still lists it, and an unnamed column
 * would be a heading nobody could act on.
 *
 * @param leases - The leases to group, already filtered and searched.
 * @returns One bucket per column, in board order.
 */
export function groupLeasesByColumn<T extends { state: string }>(
  leases: readonly T[],
): Array<{ id: LeaseBoardColumnId; leases: T[] }> {
  const buckets = new Map<LeaseBoardColumnId, T[]>(
    LEASE_BOARD_COLUMNS.map((column) => [column.id, [] as T[]]),
  );
  leases.forEach((lease) => {
    const id = columnForLeaseState(lease.state);
    if (id) buckets.get(id)?.push(lease);
  });
  return LEASE_BOARD_COLUMNS.map((column) => ({
    id: column.id,
    leases: buckets.get(column.id) ?? [],
  }));
}

/**
 * Resolve the display label for a board column.
 *
 * @returns A translator from column id to localized label.
 */
export function useLeaseBoardColumnLabel(): (id: LeaseBoardColumnId) => string {
  const { t } = useTranslation('features/leases');
  return (id) => t(`board.columns.${id}`);
}
